import React, { useEffect, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { normalizedName } from './CompanyName'
import CompanyApi from '../../api/CompanyApi'
import { CompanyEntity } from 'types/CompanyEntity'

type CompanyOption = CompanyEntity & { name: string }

const companyMatchesSearch = (company: CompanyOption, query: string) => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) {
        return true
    }

    const name = normalizedName(company.name)?.toLowerCase() ?? ''
    if (name.includes(normalizedQuery)) {
        return true
    }

    return company.shares?.some((share) => share.symbol?.toLowerCase().includes(normalizedQuery)) ?? false
}

function CompanySelect({
    companyId,
    onSelectCompany,
}: {
    companyId?: string
    onSelectCompany: (v: Pick<CompanyEntity, 'id' | 'name'> | null) => void
}) {
    const [companies, setCompanies] = useState<CompanyOption[] | null>(null)
    const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null)
    const companyApi = new CompanyApi()

    useEffect(() => {
        const fetchData = async () => {
            const fetchedCompanies = await companyApi.findAll()
            const mappedCompanies = fetchedCompanies
                .map((c, index, arr) => {
                    let name = c.name ?? 'Namn saknas'
                    const prevOccurencies = arr.slice(0, index).filter((_c) => _c.name === c.name)

                    if (prevOccurencies.length) {
                        name += ` (${prevOccurencies.length + 1})`
                    }

                    return {
                        ...c,
                        name,
                    }
                })
                .sort((a, b) => a.name.localeCompare(b.name))
            setCompanies(mappedCompanies)
        }
        if (!companies) {
            void fetchData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!companies) return

        if (!companyId) {
            setSelectedCompany(null)
            return
        }

        setSelectedCompany(companies.find((company) => company.id === companyId) ?? null)
    }, [companies, companyId])

    if (!companies) return null

    return (
        <Autocomplete
            value={selectedCompany}
            onChange={(_event, newValue) => {
                setSelectedCompany(newValue)
                onSelectCompany(newValue)
            }}
            options={companies}
            getOptionLabel={(company) => normalizedName(company.name) as string}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options, { inputValue }) =>
                options.filter((company) => companyMatchesSearch(company, inputValue))
            }
            renderOption={(props, company) => {
                const { key, ...optionProps } = props

                return (
                    <Box component='li' key={key} {...optionProps} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='body2'>{normalizedName(company.name)}</Typography>
                    </Box>
                )
            }}
            renderInput={(params) => <TextField {...params} label='Välj bolag' />}
        />
    )
}

export { CompanySelect }
